import React, { useState } from 'react';
import { Select, Modal, Form, Input, Radio } from 'antd';
import type { SelectProps } from 'antd/es/select';
import { BusinessModel, BusinessModelCreate } from '../../types/businessModel';
import './BusinessModelSelector.css';

interface BusinessModelSelectorProps {
  models: BusinessModel[];
  currentModel: BusinessModel | null;
  onModelSelect: (model: BusinessModel | null) => void;
  onCreateModel?: (model: BusinessModelCreate) => Promise<void>;
  artifactTreeId: string;
}

const BusinessModelSelector: React.FC<BusinessModelSelectorProps> = ({
  models,
  currentModel,
  onModelSelect,
  onCreateModel,
  artifactTreeId,
}) => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Track whether user selected "Create New" or "Copy Existing"
  const [showCopyForm, setShowCopyForm] = useState(false);

  const handleChange: SelectProps['onChange'] = (value) => {
    if (value === 'create_new') {
      // Reset form and default creation path to "new"
      form.resetFields();
      form.setFieldsValue({ creationType: 'new' });
      setShowCopyForm(false);
      setIsModalVisible(true);
    } else {
      const selectedModel = models.find((model) => model.id === value);
      onModelSelect(selectedModel || null);
    }
  };

  const handleModalOk = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      console.log('[BusinessModelSelector] Form values:', values);
      
      if (!onCreateModel) {
        console.error('[BusinessModelSelector] onCreateModel callback not provided');
        Modal.error({
          title: 'Error',
          content: 'Create model handler not available',
        });
        setIsSubmitting(false);
        return;
      }
      
      console.log('[BusinessModelSelector] Calling onCreateModel with:', {
        ...values,
        artifact_tree_id: artifactTreeId,
      });
      
      await onCreateModel({
        ...values,
        artifact_tree_id: artifactTreeId,
      });
      
      console.log('[BusinessModelSelector] Business model created successfully');
      setIsModalVisible(false);
      form.resetFields();
      setShowCopyForm(false);
    } catch (error: any) {
      // Check if this is a validation error (has errorFields)
      if (error?.errorFields) {
        // Validation error - don't show error modal, just let form show validation messages
        console.log('[BusinessModelSelector] Form validation failed:', error.errorFields);
        setIsSubmitting(false);
        return; // Don't close modal on validation error
      }
      
      // Actual API/creation error
      console.error('[BusinessModelSelector] Error creating business model:', error);
      Modal.error({
        title: 'Failed to Create Business Model',
        content: error?.response?.data?.detail || 
                 error?.response?.data?.message || 
                 error?.message || 
                 'An error occurred while creating the business model. Please try again.',
      });
      setIsSubmitting(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setShowCopyForm(false);
  };

  const options = [
    ...models.map((model) => ({
      label: model.name,
      value: model.id,
    })),
    {
      label: '+ Create New',
      value: 'create_new',
    },
  ];

  return (
    <>
      <Select
        style={{ width: 200 }}
        placeholder="Select a business model"
        value={currentModel?.id}
        onChange={handleChange}
        options={options}
      />
      <Modal
        title="Create New Business Model"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={isSubmitting}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                message: 'Please enter a name',
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="creationType"
            label="Creation Type"
            initialValue="new"
            rules={[{ required: true, message: 'Please select a creation type' }]}
          >
            <Radio.Group onChange={(e) => setShowCopyForm(e.target.value === 'copy')}>
              <Radio value="new">Create New</Radio>
              <Radio value="copy">Copy Existing</Radio>
            </Radio.Group>
          </Form.Item>
          {showCopyForm && (
            <Form.Item
              name="copy_from_model_id"
              label="Copy From Model"
              rules={[{ required: true, message: 'Please select a model to copy from' }]}
            >
              <Select placeholder="Select a model to copy from">
                {models.map((model) => (
                  <Select.Option key={model.id} value={model.id}>
                    {model.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default BusinessModelSelector;
